import { useRef, type KeyboardEvent, type ReactNode } from "react";

import { useGlide } from "@/lib/useGlide";

import styles from "./Segmented.module.css";

export interface SegmentOption<V extends string> {
  value: V;
  label: ReactNode;
  disabled?: boolean;
  /** The accessible name, where the visible label is not enough on its own. */
  ariaLabel?: string;
}

interface SegmentedControlProps<V extends string> {
  /** The group's accessible name — or `labelledBy`, the id of a visible one. */
  label?: string;
  labelledBy?: string;
  options: readonly SegmentOption<V>[];
  value: V;
  onChange: (value: V) => void;
  /**
   * Held, not gone: every radio says `aria-disabled` and a click or a key
   * does nothing, but the focus stays where it is and the radios stay
   * reachable, so the reason can be read (record fix wave, T8).
   */
  disabled?: boolean;
  className?: string;
  /**
   * `follow` (default): the choice follows the focus, as a radio group does.
   * `manual`: the arrows, Home and End only move the focus; Space, Enter or
   * a click choose — for a choice that throws work away (the outline's
   * „Ausgangsform“, Task 17 review T17-2), which must not happen to a
   * person passing over it (WCAG 3.2.2).
   */
  activation?: "follow" | "manual";
}

/**
 * A radio group drawn as joined segments, with a thumb that glides behind the
 * checked one (BW-5). One tab stop (the checked radio); all four arrows move
 * and choose, wrapping at the ends, Home and End go to the ends — the choice
 * follows the focus, unless `activation="manual"` leaves it to Space, Enter
 * or a click. Segments that are `disabled` are skipped.
 *
 * The group carries `data-thumb`: only then does `Segmented.module.css` take
 * the checked radio's own ink away. The groups that are toggles or filters
 * (`aria-pressed`, a Tab stop each) share that stylesheet without a thumb and
 * keep the ink on the segment.
 */
export function SegmentedControl<V extends string>({
  label,
  labelledBy,
  options,
  value,
  onChange,
  disabled,
  className,
  activation = "follow",
}: SegmentedControlProps<V>) {
  const groupRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  useGlide("radio", groupRef, thumbRef, options.length, value);

  const checked = options.findIndex((option) => option.value === value);
  // A value that is none of the options still leaves the group reachable.
  const stop = checked >= 0 ? checked : 0;

  function choose(index: number) {
    const option = options[index];
    if (option === undefined || option.disabled === true || disabled === true) return;
    onChange(option.value);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    // Alt+← is the browser's Back, not a segment: a modified key is not ours.
    if (event.altKey || event.ctrlKey || event.metaKey) return;
    const forward = event.key === "ArrowRight" || event.key === "ArrowDown";
    const backward = event.key === "ArrowLeft" || event.key === "ArrowUp";
    if (!forward && !backward && event.key !== "Home" && event.key !== "End") return;
    event.preventDefault();
    if (disabled === true) return;
    const count = options.length;
    const radios = groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]');
    // Manual, the focus wanders on its own, and the next step starts where it is.
    const focused =
      activation === "manual" && radios !== undefined
        ? Array.from(radios).findIndex((radio) => radio === document.activeElement)
        : -1;
    const from = focused >= 0 ? focused : checked;
    // Home and End look from beyond their end, so a disabled first or last
    // option is stepped over like any other; with nothing checked, a step
    // forward starts before the first and a step back at the first.
    let target = event.key === "Home" ? -1 : event.key === "End" ? count : from >= 0 ? from : forward ? -1 : 0;
    const step = forward || event.key === "Home" ? 1 : -1;
    for (let tries = 0; tries < count; tries += 1) {
      target = (target + step + count) % count;
      if (options[target]?.disabled !== true) break;
    }
    const option = options[target];
    if (option === undefined || option.disabled === true) return;
    if (activation === "follow") choose(target);
    radios?.[target]?.focus();
  }

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      aria-label={label}
      aria-labelledby={labelledBy}
      className={className ? `${styles.group} ${className}` : styles.group}
      data-thumb=""
      onKeyDown={onKeyDown}
    >
      {options.map((option, index) => (
        <button
          key={option.value}
          type="button"
          role="radio"
          aria-checked={index === checked}
          aria-label={option.ariaLabel}
          aria-disabled={disabled === true || option.disabled === true ? "true" : undefined}
          tabIndex={index === stop ? 0 : -1}
          className={styles.segment}
          onClick={() => choose(index)}
        >
          {option.label}
        </button>
      ))}
      {/* After the radios, so the first radio is still the group's first
          child (`.segment:first-child` drops its left rule). */}
      <span ref={thumbRef} className={styles.thumb} aria-hidden="true" />
    </div>
  );
}
